import { GridRowModes, GridRowModesModel, GridRowSelectionModel } from "@mui/x-data-grid-premium";
import RowFeedback from "@/Common/helpers/RowFeedback";
import { RootState } from "@/redux/store";
import { IRow } from "@/Settings/types/SystemSettings/appendingConfiguration";
import { compact, map, pick } from "lodash-es";
import { useSelector } from "react-redux";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import {
	IAppendingConfigurationEntry,
	IAppendingConfigurationFranchise,
	IAppendingConfigurationItem,
	IAppendingConfigurationOems,
	IAppendingConfigurationReference,
	IAppendingProperties,
	IGLAccountElement,
	IGroupInfo,
} from "@/Settings/types/Common/model";

export const removeStartIfMatches = (inputString: string, substringToRemove: string) => {
	if (inputString.startsWith(substringToRemove)) {
		return inputString
			.slice(substringToRemove.length)
			.split(" ")
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(" ");
	}
	return inputString;
};

export const resolveOems = () => {
	const location = useSelector((state: RootState) => state.location.location);
	return map(location?.oems, (oemItem) => {
		const {
			oemReference,
			dealerCodeFS,
			version,
			financialStatementVersion: fsVersion,
			franchises,
		} = pick(oemItem, ["oemReference", "version", "financialStatementVersion", "franchises", "dealerCodeFS"]);
		const newFranchises = franchises.map((franchiseItem) => pick(franchiseItem, ["franchise", "dealerCode", "groups", "isCombined"]));
		return {
			oemReference,
			dealerCodeFS,
			version,
			fsVersion,
			franchises: newFranchises.map((item) => item.franchise),
			franchisesObjects: newFranchises,
		};
	});
};

export const splitCamelCaseAndUpperCase = (inputString = "") => {
	const parts = inputString.split(/(?=[A-Z])/);
	return parts.map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");
};

export const resolveIconWidth = (franchise: string) => {
	const franchiseIconWidthMap: { [key: string]: number } = {
		mitsubishi: 100,
		"volvo cars": 150,
		chevrolet: 150,
		dodge: 100,
		ram: 100,
		chrysler: 100,
		cadillac: 180,
		jeep: 50,
		fiat: 50,
		"alfa romeo": 50,
	};
	const fifty = 50;
	const lowerFranchise = (franchise || "").toLowerCase();
	return franchiseIconWidthMap[lowerFranchise] || fifty;
};

const DEFAULT_TEXT_COLUMN_WIDTH = 130;
const TEXT_COLUMN_WIDTH = 225;

export const resolveTextColumnWidth = ({ hideGroups }: { hideGroups: boolean }) => {
	if (hideGroups) {
		return TEXT_COLUMN_WIDTH;
	}
	return DEFAULT_TEXT_COLUMN_WIDTH;
};

export const formatAccountDescription = (row: {
	accountDescription: string;
	arithmeticExpression: { operation: string; value: number };
}) => {
	const { arithmeticExpression } = row || {};
	let { operation, value } = arithmeticExpression || {};
	if (!value && ["+", "-"].includes(operation)) {
		return row;
	}
	if (value === 1 && ["*", "/"].includes(operation)) {
		return row;
	}
	if (operation === "*") {
		operation = "×";
	}
	return {
		...row,
		accountDescription: `${row.accountDescription} ( ${operation} ${value} )`,
	};
};

export const resolveChargeBackIconColor = ({ rows, selectedRows }: { rows: IRow[]; selectedRows: GridRowSelectionModel }) => {
	const filteredRows = rows?.filter((row) => selectedRows?.includes(row?._id));
	return filteredRows?.every((row) => row?.properties?.isChargeback) ? "[&_path]:!fill-[#2357be]" : "[&_path]:!fill-[#666666]";
};

export const formatRowModesModel = (
	rowModesModel: GridRowModesModel,
	options: { mode: GridRowModes.View; ignoreModifications: boolean }
) => {
	const newObject: GridRowModesModel = {};
	for (const key in rowModesModel) {
		newObject[key] = options;
	}
	return newObject;
};

export const displaySkeleton = ({
	ids,
	action,
	apiRef,
}: {
	ids: string[];
	action: string;
	apiRef: React.MutableRefObject<GridApiPremium>;
}) => {
	switch (action) {
		case "display":
			for (const id of ids) {
				RowFeedback?.skeleton(id, apiRef)?.display();
			}
			break;
		case "success":
			for (const id of ids) {
				RowFeedback?.skeleton(id, apiRef)?.success();
			}
			break;
		default:
			break;
	}
};

export const substituteRowsById = (firstArray: IRow[], secondArray: IRow[]) => {
	const secondArrayMap = new Map(secondArray.map((item) => [item._id, item]));
	return firstArray.map((item) => secondArrayMap.get(item._id) ?? item);
};

export const replaceObjectById = ({
	clonedAppendingConfigurationInfo,
	row,
}: {
	clonedAppendingConfigurationInfo: IAppendingConfigurationReference[];
	row: Partial<IRow>;
}) => {
	const idToReplace = row?._id ?? "";
	return clonedAppendingConfigurationInfo.map((item) => {
		return {
			...item,
			oems: item.oems.map((oem) => {
				return {
					...oem,
					configuration: oem.configuration.map((config) => {
						const newConfig = { ...config };
						const index = newConfig.data.findIndex((obj) => obj._id === idToReplace);
						const arithmeticExpression = row?.arithmeticExpression ? row?.arithmeticExpression : { operation: "+", value: 0 };
						if (index !== -1) {
							newConfig.data[index] = {
								...row,
								_id: idToReplace,
								arithmeticExpression,
								accountDescription: row.accountDescription as string,
								glAccount: row.glAccount as IGLAccountElement[],
								fsAccount: row.fsAccount as string[],
								groupInfo: row.groupInfo as IGroupInfo,
								properties: row.properties as IAppendingProperties,
							};
						}
						return newConfig;
					}),
				};
			}),
		};
	});
};

export const addObjectToConfiguration = ({
	clonedAppendingConfigurationInfo,
	reference,
	oemReference,
	dealerCodeFS,
	row,
	franchise,
}: {
	clonedAppendingConfigurationInfo: IAppendingConfigurationReference[];
	reference: string;
	oemReference?: string | null;
	dealerCodeFS?: string | null;
	row: Partial<IRow>;
	franchise?: string | null;
}) => {
	return clonedAppendingConfigurationInfo.map((item) => {
		if (item.reference === reference) {
			const updatedOems = item.oems.map((oem) => {
				if (oem.oemReference === oemReference && oem.dealerCodeFS === dealerCodeFS) {
					const oemConfiguration = addObjectToData({ reference, franchise, row, oemConfiguration: oem.configuration });
					return {
						...oem,
						configuration: oemConfiguration,
					};
				}
				return oem;
			});
			return { ...item, oems: updatedOems };
		}
		return item;
	});
};

const addObjectToData = ({
	reference,
	oemConfiguration,
	franchise,
	row,
}: {
	oemConfiguration: IAppendingConfigurationFranchise[];
	reference: string;
	row: Partial<IRow>;
	franchise?: string | null;
}) => {
	if (reference === "newCarsSales") {
		return oemConfiguration.map((oemConfigurationItem) => {
			if (oemConfigurationItem.franchise === franchise) {
				const newData = [...oemConfigurationItem.data, row];
				return { ...oemConfigurationItem, data: newData };
			}
			return oemConfigurationItem;
		});
	} else {
		return oemConfiguration.map((oemConfigurationItem) => {
			const newData = [...oemConfigurationItem.data, row];
			return { ...oemConfigurationItem, data: newData };
		});
	}
};

export const clearAppendingConfig = (
	newAppendingConfigurationInfo: {
		oems: {
			configuration: {
				data: (Partial<IRow> | IAppendingConfigurationEntry)[];
				_id: string;
				franchise: string;
				isCombined?: boolean;
				groups?: {}[];
			}[];
			fsVersion: string;
		}[];
		_id: string;
		reference: string;
	}[]
) => {
	return compact(
		newAppendingConfigurationInfo?.map((item) => {
			const appendingOems = compact(
				item.oems?.map((oem) => {
					const oemConfig = compact(
						oem.configuration?.map((config) => {
							const { data } = config;
							if (data.length) {
								const { isCombined, groups, ...rest } = config;
								return rest;
							}
							return null;
						})
					);
					if (oemConfig.length) {
						return {
							...oem,
							configuration: oemConfig,
						};
					}
					return null;
				})
			);
			if (appendingOems.length) {
				return {
					...item,
					oems: appendingOems,
				};
			}
			return null;
		})
	);
};

export const removeObjectFromIds = (data: IAppendingConfigurationReference[], ids: string[]) => {
	return data.map((item) => {
		return {
			...item,
			oems: item.oems.map((oem) => {
				return {
					...oem,
					configuration: oem.configuration.map((config) => {
						return { ...config, data: config.data.filter((d) => !ids.includes(d._id)) };
					}),
				};
			}),
		};
	});
};

export const extractGlAccounts = (data: IAppendingConfigurationReference[]) => {
	return data.flatMap((department) =>
		department.oems.flatMap((oem) =>
			oem.configuration.flatMap((config) =>
				config.data.flatMap((item) => (item.glAccount ? item.glAccount.map((account) => account.glAccount) : []))
			)
		)
	);
};

export const updateOriginalArray = (originalArray: IAppendingConfigurationItem[], updatedObject: IAppendingConfigurationItem[]) => {
	return (
		[...originalArray]?.map((originalItem) => {
			const updatedPositionItem = updatedObject.find((updatedItem) => updatedItem.position === originalItem.position);
			for (const dataItem of originalItem.data) {
				const updatedReferenceItem = updatedPositionItem?.data.find((updatedItem) => updatedItem.reference === dataItem.reference);
				const areOemsEqual = arraysEqualByValue(dataItem.oems, updatedReferenceItem?.oems ?? []);
				if (!areOemsEqual) {
					dataItem.oems = updatedReferenceItem?.oems ?? [];
				}
			}
			return originalItem;
		}) ?? []
	);
};

export function deepEqualWithoutId(obj1: any, obj2: any) {
	if (typeof obj1 !== "object" || typeof obj2 !== "object" || obj1 === null || obj2 === null) {
		return obj1 === obj2;
	}
	const keys1 = Object.keys(obj1).filter((key) => key !== "_id");
	const keys2 = Object.keys(obj2).filter((key) => key !== "_id");
	if (keys1.length !== keys2.length) {
		return false;
	}
	for (const key of keys1) {
		if (!deepEqualWithoutId(obj1[key], obj2[key])) {
			return false;
		}
	}
	return true;
}

export function arraysEqualByValue(arr1: IAppendingConfigurationOems[], arr2: IAppendingConfigurationOems[]) {
	if (arr1.length !== arr2.length) {
		return false;
	}
	for (let i = 0; i < arr1.length; i++) {
		if (!deepEqualWithoutId(arr1[i], arr2[i])) {
			return false;
		}
	}
	return true;
}
